import React, { useState, useEffect } from 'react';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { RiskCard, RiskCardHeader, RiskCardBody, RiskChip, RiskButton } from '../../components/risk/UiKit';
import OfficerNotificationModal from '../../components/risk/OfficerNotificationModal';
import NotificationHistory from '../../components/risk/NotificationHistory';
import MLPredictionCard from '../../components/risk/MLPredictionCard';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function Assessments({ onAssessmentSelect }) {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [canCreate, setCanCreate] = useState(false);
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeYear, setActiveYear] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    assessment_year: new Date().getFullYear()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  
  // Notification system state
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [canNotify, setCanNotify] = useState(false);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments`, {
        credentials: 'include'
      });
      const data = await res.json();
      if (data.success) {
        setAssessments(data.assessments);
        if (typeof data.permissions?.canCreate === 'boolean') {
          setCanCreate(data.permissions.canCreate);
        }
      } else {
        setMessage('❌ Failed to load assessments');
      }
    } catch (err) {
      setMessage('❌ Error loading assessments');
      console.error(err);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'assessment_year' ? parseInt(value) || new Date().getFullYear() : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setMessage('❌ Assessment title is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      
      if (data.success) {
        setMessage('✅ Assessment created successfully');
        setFormData({ title: '', assessment_year: new Date().getFullYear() });
        setShowCreateForm(false);
        fetchAssessments();
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`❌ ${data.message}`);
      }
    } catch (err) {
      setMessage('❌ Network error occurred');
      console.error(err);
    }
    setIsSubmitting(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      active: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Active' },
      compiled: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Compiled' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' },
      published: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Published' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleAssessmentClick = (assessment) => {
    if (onAssessmentSelect) {
      onAssessmentSelect(assessment);
    } else {
      // Fallback to direct navigation if no callback provided
      window.location.href = `/risk/assessments/${assessment.id}`;
    }
  };

  // Notification system functions
  const handleAssessmentSelect = (assessmentId, isSelected) => {
    if (isSelected) {
      setSelectedAssessments(prev => [...prev, assessmentId]);
    } else {
      setSelectedAssessments(prev => prev.filter(id => id !== assessmentId));
    }
  };

  const handleSelectAll = () => {
    const compiledAssessments = filteredAssessments.filter(a => a.status === 'compiled');
    if (selectedAssessments.length === compiledAssessments.length) {
      setSelectedAssessments([]);
    } else {
      setSelectedAssessments(compiledAssessments.map(a => a.id));
    }
  };

  const handleNotifyOfficers = () => {
    setShowNotificationModal(true);
  };

  const handleNotificationSuccess = (message) => {
    setMessage(`✅ ${message}`);
    setTimeout(() => setMessage(''), 5000);
    setSelectedAssessments([]);
  };

  // Derived UI state for Categories (status/year)
  const statusOptions = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'draft', label: 'Draft' },
    { key: 'compiled', label: 'Compiled' },
    { key: 'approved', label: 'Approved' },
    { key: 'published', label: 'Published' }
  ];
  const yearOptions = Array.from(new Set(assessments.map(a => a.assessment_year)))
    .sort((a, b) => b - a);
  const filteredAssessments = assessments.filter(a =>
    (activeStatus === 'all' || a.status === activeStatus) &&
    (activeYear === 'all' || a.assessment_year === activeYear)
  );

  // Notification system derived state
  const compiledAssessments = filteredAssessments.filter(a => a.status === 'compiled');
  const allSelected = compiledAssessments.length > 0 && selectedAssessments.length === compiledAssessments.length;

  // Hide create button for non-admin/non-officer risk roles
  // canCreate now comes from server permissions

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      <div className="max-w-7xl mx-auto p-8">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Risk Assessments</h1>
              <p className="text-gray-600">
                Manage annual risk assessments and track their progress
              </p>
            </div>
            {canCreate && (
              <RiskButton onClick={() => setShowCreateForm(true)} className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700">
                Create Assessment
              </RiskButton>
            )}
          </div>
          <PhiFriendlyNote className="mt-4" />
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl text-sm font-medium ${
            message.startsWith('✅') 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message}
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left: Categories */}
          <div className="lg:col-span-3 space-y-6">
          <RiskCard>
            <RiskCardHeader title="Categories" subtitle="Filter by status and year" />
            <RiskCardBody className="space-y-6">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.map(opt => (
                    <RiskChip key={opt.key} active={activeStatus === opt.key} onClick={() => setActiveStatus(opt.key)}>
                      {opt.label}
                    </RiskChip>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Year</div>
                <div className="flex flex-wrap gap-2">
                  <RiskChip active={activeYear === 'all'} onClick={() => setActiveYear('all')}>All</RiskChip>
                  {yearOptions.map(y => (
                    <RiskChip key={y} active={activeYear === y} onClick={() => setActiveYear(y)}>
                      {y}
                    </RiskChip>
                  ))}
                </div>
              </div>
            </RiskCardBody>
          </RiskCard>

          {/* Quick Stats */}
          {assessments.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Total', value: assessments.length },
                { label: 'Active', value: assessments.filter(a => a.status === 'active').length },
                { label: 'Compiled', value: assessments.filter(a => a.status === 'compiled').length },
                { label: 'Published', value: assessments.filter(a => a.status === 'published').length }
              ].map((stat) => (
                <RiskCard key={stat.label}>
                  <RiskCardBody>
                    <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                    <div className="text-sm text-slate-600">{stat.label}</div>
                  </RiskCardBody>
                </RiskCard>
              ))}
            </div>
          )}
        </div>

          {/* Right: Main */}
          <div className="lg:col-span-9 space-y-6">
          {/* Create Assessment Form */}
          {showCreateForm && (
            <RiskCard>
              <RiskCardHeader title="Create New Assessment" actions={(
                <button onClick={() => setShowCreateForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors">✕</button>
              )} />
              <RiskCardBody>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-2">
                        Assessment Title *
                      </label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="e.g., Annual Risk Assessment 2025"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label htmlFor="assessment_year" className="block text-sm font-medium text-slate-700 mb-2">
                        Assessment Year
                      </label>
                      <input
                        type="number"
                        id="assessment_year"
                        name="assessment_year"
                        value={formData.assessment_year}
                        onChange={handleChange}
                        min="2020"
                        max="2030"
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.title.trim()}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Assessment'}
                    </button>
                  </div>
                </form>
              </RiskCardBody>
            </RiskCard>
          )}

          {/* Assessments List */}
          <RiskCard>
            <RiskCardHeader
              title="Your Assessments"
              subtitle={filteredAssessments.length !== assessments.length ? `${filteredAssessments.length} of ${assessments.length} shown` : `${assessments.length} total`}
              actions={canCreate ? (
                <RiskButton variant="outline" onClick={() => setShowCreateForm(true)}>New</RiskButton>
              ) : null}
            />

            {/* Notification System Interface */}
            {compiledAssessments.length > 0 && (
              <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-blue-900">
                        Select All Compiled ({compiledAssessments.length})
                      </span>
                    </label>
                    {selectedAssessments.length > 0 && (
                      <span className="text-sm text-blue-700">
                        {selectedAssessments.length} selected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowHistoryModal(true)}
                      className="px-3 py-2 text-blue-600 text-sm font-medium border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      View History
                    </button>
                    <button
                      onClick={handleNotifyOfficers}
                      disabled={selectedAssessments.length === 0}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Notify Officers ({selectedAssessments.length})
                    </button>
                  </div>
                </div>
              </div>
            )}

            {assessments.length === 0 ? (
              <RiskCardBody>
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-slate-900 mb-2">No assessments yet</h4>
                  <p className="text-slate-600 mb-4">Create your first risk assessment to get started</p>
                  {canCreate && (
                    <RiskButton onClick={() => setShowCreateForm(true)}>Create Assessment</RiskButton>
                  )}
                </div>
              </RiskCardBody>
            ) : (
              <div className="p-6">
                {filteredAssessments.length === 0 ? (
                  <div className="text-center text-slate-600">
                    No assessments match the selected category.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredAssessments.map((assessment) => (
                      <div key={assessment.id} className="space-y-3">
                        {/* Assessment Card */}
                        <div
                          className="group bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md transition-all"
                        >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3 flex-1">
                            {assessment.status === 'compiled' && (
                              <input
                                type="checkbox"
                                checked={selectedAssessments.includes(assessment.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleAssessmentSelect(assessment.id, e.target.checked);
                                }}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                              />
                            )}
                            <div 
                              className="flex-1 cursor-pointer"
                              onClick={() => handleAssessmentClick(assessment)}
                            >
                              <h4 className="text-base font-semibold text-slate-900 mr-2 truncate">{assessment.title}</h4>
                            </div>
                          </div>
                          {getStatusBadge(assessment.status)}
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <div className="flex items-center justify-between">
                            <span>Year: {assessment.assessment_year}</span>
                            <span className="text-slate-400 group-hover:text-slate-600 transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </span>
                          </div>
                          <div>Created by: {assessment.created_by_name}</div>
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            <span>Departments: {assessment.department_count || 0}</span>
                            <span>FDRs: {assessment.fdr_count || 0}</span>
                          </div>
                          <div className="mt-2 text-xs text-slate-500">
                            Created: {new Date(assessment.created_at).toLocaleDateString()}
                            {assessment.compiled_at && (
                              <span className="ml-4">
                                Compiled: {new Date(assessment.compiled_at).toLocaleDateString()}
                              </span>
                            )}
                            {assessment.approved_at && (
                              <span className="ml-4">
                                Approved: {new Date(assessment.approved_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        </div>
                        
                        {/* ML Prediction Card */}
                        <MLPredictionCard 
                          assessmentId={assessment.id} 
                          autoTrigger={assessment.status === 'compiled' || assessment.status === 'active'}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </RiskCard>
          </div>
        </div>
      </div>

      {/* Officer Notification Modal */}
      <OfficerNotificationModal
        isOpen={showNotificationModal}
        onClose={() => setShowNotificationModal(false)}
        selectedAssessments={selectedAssessments}
        assessments={assessments}
        onSuccess={handleNotificationSuccess}
      />

      {/* Notification History Modal */}
      <NotificationHistory
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
      />
    </div>
  );
}
